import React, { useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import './CompactVorderseiteDialog.css';

// CSS für Pulse-Animation
const pulseStyle = document.createElement('style');
pulseStyle.textContent = `
  @keyframes pulse {
    0%, 100% { transform: scale(1); }
    50% { transform: scale(1.1); }
  }
`;
if (!document.head.querySelector('style[data-pulse-animation]')) {
  pulseStyle.setAttribute('data-pulse-animation', 'true');
  document.head.appendChild(pulseStyle);
}

/**
 * Dialog für die Vorderseite in der Portrait/Kompakt-Ansicht.
 * Overlay + Modal mit Kopf (Titel, Schriftgröße a/A/1/⌨, × Schließen) und scrollbarem Inhalt.
 * Kopf drucktauglich: overflow vermieden, Felder nicht über den Rand.
 * Beim Öffnen: Fokus auf erstes Eingabefeld, damit der erste Klick ins Feld geht.
 * Header-Buttons: onMouseDown preventDefault, damit ein Klick reicht auch wenn Fokus im Textfeld steht.
 */
function CompactVorderseiteDialog({
  open,
  onClose,
  darkMode = false,
  isLoading = false,
  fontScale = 1,
  onFontScaleChange,
  onFontScaleOptimal,
  onSave = null, // Optional: Speicher-Button im Header
  isSaving = false,
  showSavedHint = false,
  getTabIndex = null, // Optional: Funktion zur Berechnung von TabIndex-Werten (key 'header' = Tab-Reihenfolge nach Form)
  children
}) {
  const { t } = useTranslation();
  const bodyRef = useRef(null);
  const modalRef = useRef(null);
  const previousActiveElementRef = useRef(null);
  const onCloseRef = useRef(onClose);
  const initialFocusDoneRef = useRef(false);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) {
      initialFocusDoneRef.current = false;
      return;
    }

    // Speichere das vorherige fokussierte Element
    previousActiveElementRef.current = document.activeElement;

    const modal = modalRef.current;
    if (!modal) return;

    // Finde alle fokussierbaren Elemente im Dialog, sortiert nach tabIndex
    const getFocusableElements = () => {
      const selector = 'button, [href], input:not([readonly]):not([disabled]), select:not([disabled]), textarea:not([readonly]):not([disabled]), [tabindex]:not([tabindex="-1"])';
      const elements = Array.from(modal.querySelectorAll(selector)).filter(
        el => el.offsetParent !== null && !el.disabled && el.tabIndex !== -1
      );
      elements.sort((a, b) => {
        const aTab = a.tabIndex || 0;
        const bTab = b.tabIndex || 0;
        if (aTab === 0 && bTab === 0) {
          const position = a.compareDocumentPosition(b);
          return position & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1;
        }
        return aTab - bTab;
      });
      return elements;
    };

    const handleTabKey = (e) => {
      if (e.key !== 'Tab') return;

      const focusableElements = getFocusableElements();

      if (focusableElements.length === 0) return;

      e.preventDefault();
      let currentIndex = focusableElements.findIndex(el => el === document.activeElement);
      if (currentIndex === -1 || !modal.contains(document.activeElement)) {
        currentIndex = e.shiftKey ? 0 : focusableElements.length - 1;
      }

      if (e.shiftKey) {
        const prevIndex = currentIndex <= 0 ? focusableElements.length - 1 : currentIndex - 1;
        focusableElements[prevIndex].focus();
      } else {
        const nextIndex = currentIndex >= focusableElements.length - 1 ? 0 : currentIndex + 1;
        focusableElements[nextIndex].focus();
      }
    };

    // Escape-Taste: Dialog schließen
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        onCloseRef.current?.();
      }
    };

    // Capture-Phase: Tab abfangen bevor der Browser den Fokus verschiebt
    modal.addEventListener('keydown', handleTabKey, true);
    document.addEventListener('keydown', handleEscape);

    // Fokus auf erstes fokussierbares Element im Dialog-Body nur einmal pro Öffnen
    if (!isLoading && !initialFocusDoneRef.current) {
      let cancelled = false;
      const run = () => {
        if (cancelled) return;
        initialFocusDoneRef.current = true;
        const focusable = getFocusableElements();
        const body = bodyRef.current;
        const firstInBody = body ? focusable.find(el => body.contains(el)) : null;
        const toFocus = firstInBody || focusable[0];
        if (toFocus && typeof toFocus.focus === 'function') {
          toFocus.focus();
        } else {
          modal.focus();
        }
      };
      const t = setTimeout(() => requestAnimationFrame(() => requestAnimationFrame(run)), 100);
      return () => {
        cancelled = true;
        clearTimeout(t);
        modal.removeEventListener('keydown', handleTabKey, true);
        document.removeEventListener('keydown', handleEscape);
        if (previousActiveElementRef.current && typeof previousActiveElementRef.current.focus === 'function') {
          previousActiveElementRef.current.focus();
        }
      };
    }

    return () => {
      modal.removeEventListener('keydown', handleTabKey, true);
      document.removeEventListener('keydown', handleEscape);
      // Fokus zurück zum vorherigen Element
      if (previousActiveElementRef.current && typeof previousActiveElementRef.current.focus === 'function') {
        previousActiveElementRef.current.focus();
      }
    };
  }, [open, isLoading]);

  if (!open) return null;

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      e.preventDefault();
      e.stopPropagation();
      e.nativeEvent.stopImmediatePropagation();
      onClose?.();
    }
  };

  const handleCloseClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.nativeEvent.stopImmediatePropagation();
    onClose?.();
  };

  return (
    <div
      className="compact-vorderseite-dialog-overlay"
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="compact-vorderseite-dialog-title"
    >
      <div
        ref={modalRef}
        className="compact-vorderseite-dialog-modal"
        onClick={(e) => e.stopPropagation()}
        tabIndex={-1}
      >
        <header className="compact-vorderseite-dialog-header">
          <h2 id="compact-vorderseite-dialog-title" className="compact-vorderseite-dialog-title">
            Vorderseite
          </h2>
          <div className="compact-vorderseite-dialog-header-actions">
            <div className="compact-vorderseite-dialog-header-actions-left">
            {onSave && (
              <button
                type="button"
                tabIndex={getTabIndex ? getTabIndex('header', 0) : undefined}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => onSave()}
                disabled={isSaving}
                className="compact-vorderseite-dialog-btn-save"
                title={isSaving ? t('compact_vorderseite.saving') : t('compact_vorderseite.save_title')}
                aria-label={t('compact_vorderseite.save_aria')}
                style={{
                  backgroundColor: darkMode ? (isSaving ? '#4b5563' : (showSavedHint ? '#10b981' : '#059669')) : (isSaving ? '#d1d5db' : (showSavedHint ? '#22c55e' : '#10b981')),
                  color: darkMode ? '#f9fafb' : '#ffffff',
                  border: showSavedHint ? `2px solid ${darkMode ? '#86efac' : '#15803d'}` : 'none',
                  borderRadius: '6px',
                  padding: '8px 16px',
                  fontWeight: 600,
                  cursor: isSaving ? 'not-allowed' : 'pointer',
                  opacity: isSaving ? 0.7 : 1,
                  marginRight: '8px',
                  transition: 'all 0.3s ease',
                  transform: showSavedHint ? 'scale(1.05)' : 'scale(1)',
                  boxShadow: showSavedHint ? (darkMode ? '0 0 15px rgba(134, 239, 172, 0.7)' : '0 0 15px rgba(21, 128, 61, 0.7)') : 'none',
                  animation: showSavedHint ? 'pulse 0.5s ease-in-out' : 'none'
                }}
              >
                {isSaving ? t('compact_vorderseite.saving') : (showSavedHint ? t('compact_vorderseite.saved') : t('compact_vorderseite.save'))}
              </button>
            )}
            {typeof fontScale === 'number' && onFontScaleChange && (
              <>
                <button
                  type="button"
                  tabIndex={getTabIndex ? getTabIndex('header', 1) : undefined}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => onFontScaleChange((s) => Math.max(0.85, s - 0.1))}
                  title="Schrift kleiner"
                  className="compact-vorderseite-dialog-btn-scale"
                  aria-label="Schrift kleiner"
                >
                  <span style={{ fontSize: '0.8em' }}>a</span>
                </button>
                <button
                  type="button"
                  tabIndex={getTabIndex ? getTabIndex('header', 2) : undefined}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => onFontScaleChange((s) => Math.min(1.35, s + 0.1))}
                  title="Schrift größer"
                  className="compact-vorderseite-dialog-btn-scale"
                  aria-label="Schrift größer"
                >
                  A
                </button>
                <button
                  type="button"
                  tabIndex={getTabIndex ? getTabIndex('header', 3) : undefined}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => onFontScaleChange(1)}
                  title="Schriftgröße Standard (1)"
                  className={`compact-vorderseite-dialog-btn-scale ${fontScale === 1 ? 'active' : ''}`}
                  aria-label="Schriftgröße Standard"
                >
                  1
                </button>
                {onFontScaleOptimal && (
                  <button
                    type="button"
                    tabIndex={getTabIndex ? getTabIndex('header', 4) : undefined}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={onFontScaleOptimal}
                    title="Schriftgröße auf Breite anpassen"
                    className="compact-vorderseite-dialog-btn-scale"
                    aria-label="Schriftgröße anpassen"
                  >
                    ⌨
                  </button>
                )}
              </>
            )}
            </div>
            <button
              type="button"
              tabIndex={getTabIndex ? getTabIndex('header', onFontScaleOptimal ? 5 : 4) : undefined}
              onMouseDown={(e) => e.preventDefault()}
              onClick={handleCloseClick}
              className="compact-vorderseite-dialog-btn-close"
              title="Schließen"
              aria-label="Schließen"
            >
              × Schließen
            </button>
          </div>
        </header>
        <div ref={bodyRef} className="compact-vorderseite-dialog-body">
          {isLoading ? (
            <div className="compact-vorderseite-dialog-loading">
              Lade Vorderseite...
            </div>
          ) : (
            children
          )}
        </div>
      </div>
    </div>
  );
}

export default CompactVorderseiteDialog;
